import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDestinations } from '@/hooks/useDestinations';

export default function DestinationSearch() {
  const { destinations, loading, error } = useDestinations();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = destinations.filter((d) =>
    d.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(id: string) {
    setOpen(false);
    setQuery('');
    navigate(`/tours?destination=${id}`);
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl">
      {/* Input */}
      <div
        className={`flex items-center gap-3 rounded-full border px-5 py-4 transition-all bg-gradient-to-r from-accent-50 via-background-50 to-primary-50 ${
          open
            ? 'border-accent-500 ring-4 ring-accent-100/60'
            : 'border-accent-200 hover:border-accent-300'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className="ri-search-line text-accent-500 text-lg" />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t('search.placeholder')}
          className="flex-1 bg-transparent text-sm md:text-base text-foreground-950 placeholder:text-secondary-400 outline-none border-none focus:ring-0"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="w-6 h-6 flex items-center justify-center shrink-0 rounded-full bg-background-100 hover:bg-background-200 transition-colors"
            aria-label={t('search.clear')}
          >
            <i className="ri-close-line text-secondary-500 text-sm" />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (filtered.length > 0) handleSelect(filtered[0].id);
          }}
          className="shrink-0 hidden sm:inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          {t('search.button')}
        </button>
      </div>

      {/* Dropdown suggestions */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-2xl border border-background-200 bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
          <div className="max-h-[360px] overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-2 py-2">
                    <div className="w-14 h-14 shrink-0 animate-pulse rounded-lg bg-background-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-1/3 animate-pulse rounded bg-background-100" />
                      <div className="h-2 w-2/3 animate-pulse rounded bg-background-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="px-4 py-8 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-map-pin-line text-secondary-400 text-lg" />
                </div>
                <p className="mt-3 text-sm text-secondary-500">{t('search.error')}</p>
              </div>
            ) : filtered.length > 0 ? (
              filtered.map((dest) => (
                <button
                  key={dest.id}
                  type="button"
                  onClick={() => handleSelect(dest.id)}
                  className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-accent-50 transition-colors"
                >
                  <div className="w-14 h-14 shrink-0 overflow-hidden rounded-lg">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm md:text-base font-semibold text-foreground-950 truncate">
                      {dest.name}
                    </p>
                    <p className="text-xs text-secondary-500 line-clamp-1">
                      {dest.description}
                    </p>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-arrow-right-s-line text-secondary-400" />
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-map-pin-line text-secondary-400 text-lg" />
                </div>
                <p className="mt-3 text-sm text-secondary-500">
                  {t('search.noResult', { query })}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}